export const hasNonEmptyProp = <T extends object>(obj?: T, someOrKeysOrAll?: (keyof T)[] | boolean) => {
  if (!obj || typeof obj !== 'object') return false;
  if (!someOrKeysOrAll) return Object.values(obj).some(v => v !== null && v !== undefined && v !== '');
  if (someOrKeysOrAll === true) return Object.values(obj).every(v => v !== null && v !== undefined && v !== '');
  return someOrKeysOrAll.every(key => obj[key] !== null && obj[key] !== undefined && obj[key] !== '');
};

/**
 * Convert a promise to a tuple
 * @param promise
 * @returns
 * @example const [err, data] = await promiseToTuple(promise);
 */
export const promiseToTuple = async <T>(promise: Promise<T>): Promise<[undefined, Awaited<T>] | [unknown, undefined]> => {
  try {
    const data = await promise;
    return [undefined, data];
  } catch (err) {
    return [err, undefined];
  }
};

/**
 * Wait for a callback to return a truthy value
 * @param callback
 * @param timeout
 * @example await waitCallback(() => !!ethereum);
 * @returns
 */
export const waitCallback = <T>(callback: () => Promise<T> | T, timeout = 5 * 60e3) => {
  const end = Date.now() + timeout;
  const invite = 1e3;
  let lastTime = Date.now();
  let framer: number;
  return new Promise<NonNullable<Awaited<T>>>((resolve, reject) => {
    const check = async () => {
      try {
        if (lastTime + invite <= Date.now()) {
          lastTime = Date.now();
          const result = await callback();
          if (result) {
            if (framer) cancelAnimationFrame(framer);
            return resolve(result);
          }
          if (Date.now() > end) {
            if (framer) cancelAnimationFrame(framer);
            return reject(new Error('Timeout'));
          }
        }
        framer = requestAnimationFrame(check);
      } catch (error) {
        if (framer) cancelAnimationFrame(framer);
        return reject(error);
      }
    };
    framer = requestAnimationFrame(check);
  });
};

export const getRandomItems = <T>(array: T[], n: number): T[] => {
  if (!array || !Array.isArray(array) || array.length === 0) return [];
  if (n >= array.length) return [...array];
  const result = [];
  const copyArray = [...array];
  for (let i = 0; i < n; i++) {
    const randomIndex = Math.floor(Math.random() * copyArray.length);
    result.push(copyArray[randomIndex]);
    copyArray.splice(randomIndex, 1);
  }
  return result;
};

export const getTwitterShareUrl = (text: string, url: string = location.href) => {
  const twitterUrl = new URL('https://twitter.com/intent/tweet');
  twitterUrl.searchParams.set('text', text);
  twitterUrl.searchParams.set('original_referer', url);
  // twitterUrl.searchParams.set('url', url);
  return twitterUrl.href;
};

export const BigintMax = (a: bigint, b: bigint) => {
  return a > b ? a : b;
};

export const BigintMin = (a: bigint, b: bigint) => {
  return a < b ? a : b;
};
